/* src/components/suggestions/suggestionsBadge.js */

// Pure (dependency-free) badge logic for the Suggestions nav item, kept apart
// from the React hook so it can be unit-tested without pulling in Firestore.
//
// The badge reports status changes the viewer has not looked at yet:
//   • a suggestion COMPLETED since the tab was last opened → red count
//   • suggestions READ (without being completed) since then → grey count
// Completed always wins: 10 read + 1 completed shows a red "1", never an "11".
// Admins additionally get a BLUE count of unread suggestions submitted since
// then (`countNewSuggestions`), which sits alongside the red/grey count rather
// than replacing it.
//
// "Since the tab was last opened" is stored per user in localStorage (the same
// pattern TutorDashboard already uses for `notif_last_viewed_<uid>`), so the
// badge survives a reload and clears as soon as the tab is opened.
//
// A change made BY the viewer never notifies them — an admin who marks a
// suggestion completed while sitting on the Suggestions tab must not raise a
// badge for their own click. This is matched on `readByUid` / `completedByUid`
// (stamped by the mark-read / mark-completed actions) and NEVER on the display
// name: one person commonly holds both an admin and a tutor account with the
// same name, and a name match would silently swallow the tutor's notification.

const SEEN_KEY_PREFIX = "suggestions_last_viewed_";

export function suggestionsSeenKey(uid) {
  return `${SEEN_KEY_PREFIX}${uid || ""}`;
}

/** ms of the last time this user opened the Suggestions tab (0 = never). */
export function getSuggestionsSeenAt(uid) {
  if (!uid) return 0;
  try {
    const raw = localStorage.getItem(suggestionsSeenKey(uid));
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Remember that the viewer has now seen everything up to `atMs`. */
export function markSuggestionsSeen(uid, atMs = Date.now()) {
  if (!uid) return;
  try {
    localStorage.setItem(suggestionsSeenKey(uid), String(atMs));
  } catch {
    /* storage disabled (private mode) — the badge just won't persist */
  }
}

function isOwnAction(row, uid) {
  if (!uid) return false;
  const actorUid = row?.status === "completed" ? row.completedByUid : row.readByUid;
  return !!actorUid && actorUid === uid;
}

/** When the suggestion last changed status (falls back to its last update). */
export function statusChangedMs(row) {
  if (!row) return 0;
  if (row.status === "completed") return row.completedAtMs || row.updatedAtMs || 0;
  if (row.status === "read") return row.readAtMs || row.updatedAtMs || 0;
  return 0;
}

/**
 * The viewer's badge as `{ count, tone }`:
 *   "alert" → red  (suggestion(s) completed)
 *   "muted" → grey (suggestion(s) read, none completed)
 *   "none"  → nothing unseen
 */
export function countSuggestionNotifications(rows, { uid = "", seenAtMs = 0 } = {}) {
  let completed = 0;
  let read = 0;

  (rows || []).forEach((row) => {
    const at = statusChangedMs(row);
    // Unknown/unstamped changes never count — better no badge than a stale one.
    if (!at || at <= (seenAtMs || 0)) return;

    if (isOwnAction(row, uid)) return;

    if (row.status === "completed") completed += 1;
    else if (row.status === "read") read += 1;
  });

  if (completed > 0) return { count: completed, tone: "alert" };
  if (read > 0) return { count: read, tone: "muted" };
  return { count: 0, tone: "none" };
}

/**
 * Blue count (admins only): suggestions submitted since the tab was last opened
 * that nobody has read yet.
 *
 * Both conditions matter. `status === 'new'` stops the blue badge from
 * double-signalling a suggestion already represented in the red/grey count —
 * a colleague may have read it before you looked. `createdAtMs > seenAtMs`
 * keeps it a notification: opening the Suggestions tab clears it, exactly like
 * the completed/read badge. Unstamped creations (a pending server timestamp on
 * the author's own client) are not counted.
 */
export function countNewSuggestions(rows, { seenAtMs = 0 } = {}) {
  return (rows || []).filter(
    (row) => row?.status === "new" && (row.createdAtMs || 0) > (seenAtMs || 0)
  ).length;
}
